<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatReportDuration, durationMetricTooltip } from '../constants';

const props = defineProps({
  overview: {
    type: Object,
    default: () => ({}),
  },
  statusChangesActive: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['statusChangesClick']);
const { t } = useI18n();

const cards = computed(() => [
  {
    key: 'accepting',
    label: t('AGENT_ACTIVITY_REPORT.CARDS.ACCEPTING'),
    value: formatReportDuration(props.overview.accepting_seconds),
    tooltip: durationMetricTooltip(
      t,
      t('AGENT_ACTIVITY_REPORT.CARDS.TOOLTIPS.ACCEPTING'),
      props.overview.accepting_seconds
    ),
    clickable: false,
  },
  {
    key: 'not_accepting',
    label: t('AGENT_ACTIVITY_REPORT.CARDS.NOT_ACCEPTING'),
    value: formatReportDuration(props.overview.not_accepting_seconds),
    tooltip: durationMetricTooltip(
      t,
      t('AGENT_ACTIVITY_REPORT.CARDS.TOOLTIPS.NOT_ACCEPTING'),
      props.overview.not_accepting_seconds
    ),
    clickable: false,
  },
  {
    key: 'online',
    label: t('AGENT_ACTIVITY_REPORT.CARDS.ONLINE'),
    value: formatReportDuration(props.overview.online_seconds),
    tooltip: durationMetricTooltip(
      t,
      t('AGENT_ACTIVITY_REPORT.CARDS.TOOLTIPS.ONLINE'),
      props.overview.online_seconds
    ),
    clickable: false,
  },
  {
    key: 'offline',
    label: t('AGENT_ACTIVITY_REPORT.CARDS.OFFLINE'),
    value: formatReportDuration(props.overview.offline_seconds),
    tooltip: durationMetricTooltip(
      t,
      t('AGENT_ACTIVITY_REPORT.CARDS.TOOLTIPS.OFFLINE'),
      props.overview.offline_seconds
    ),
    clickable: false,
  },
  {
    key: 'status_changes',
    label: t('AGENT_ACTIVITY_REPORT.CARDS.STATUS_CHANGES'),
    value: Number(props.overview.status_change_count || 0).toLocaleString(),
    tooltip: t('AGENT_ACTIVITY_REPORT.CARDS.TOOLTIPS.STATUS_CHANGES'),
    clickable: true,
  },
]);
</script>

<template>
  <div class="mt-2 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
    <button
      v-for="card in cards"
      :key="card.key"
      type="button"
      class="rounded-xl border border-n-weak bg-n-solid-2 px-4 py-3 text-left transition"
      :class="{
        'cursor-default': !card.clickable,
        'hover:border-n-brand': card.clickable,
        'border-n-brand ring-1 ring-n-brand/30':
          card.clickable && statusChangesActive,
      }"
      :disabled="!card.clickable"
      @click="card.clickable && emit('statusChangesClick')"
    >
      <p
        class="mb-1 text-xs font-medium text-n-slate-11 inline-flex items-center gap-1"
      >
        {{ card.label }}
        <span
          v-if="card.tooltip"
          v-tooltip.right="card.tooltip"
          class="i-lucide-info flex-shrink-0 text-n-slate-10 size-3"
        />
      </p>
      <p class="mb-0 text-lg font-semibold tabular-nums text-n-slate-12">
        {{ card.value || '--' }}
      </p>
    </button>
  </div>
</template>
